import {
  Component,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import saveAs from 'file-saver';
import moment from 'moment';

import { OrderService } from 'src/app/services/order.service';
import { StorageService } from 'src/app/services/storage.service';
import { GenericHttpService } from 'src/app/services/generic-http.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { TableColumn, ColumnVisibility, DataTableComponent } from 'src/app/shared-components/data-table/data-table.component';

export interface ImportedDocumentItem {
  id: number | string;
  order_id: number | string;
  ref_id: string;
  document_name: string;
  uploaded_on: string | Date;
  imported_by: string;
  status: string;
  order_type: string;
  raw_order?: any;
}

@Component({
  selector: 'app-contract-import-documents',
  templateUrl: './contract-import-documents.component.html',
  styleUrls: ['./contract-import-documents.component.scss'],
  standalone: false,
})
export class ContractImportDocumentsComponent implements OnInit, OnDestroy {
  @ViewChild('viewDocModal') viewDocModal!: TemplateRef<any>;
  @ViewChild('downloadSuccessModal') downloadSuccessModal!: TemplateRef<any>;
  @ViewChild('dataTable') dataTable: DataTableComponent;
  tableColumns: TableColumn[] = [
    {
      field: 'document_name',
      header: 'Document Name',
      width: '24%',
      tooltip: true
    },
    {
      field: 'order_id',
      header: 'Order ID',
      width: '11%'
    },
    {
      field: 'ref_id',
      header: 'Ref. ID',
      width: '15%'
    },
    {
      field: 'uploaded_on',
      header: 'Uploaded On',
      width: '15%',
      pipe: 'date:dd/MM/yyyy hh:mm a'
    },
    {
      field: 'imported_by',
      header: 'Imported By',
      width: '13%'
    },
    {
      field: 'status',
      header: 'Status',
      width: '9%'
    },
    {
      field: 'actions',
      header: 'Actions',
      width: '13%',
      toggleable: false
    }
  ];


  userRole: string | null = null;
  accountId: string | null = null;
  currentUserName: string = 'User';

  documents: ImportedDocumentItem[] = [];
  filteredDocuments: ImportedDocumentItem[] = [];
  displayedDocuments: ImportedDocumentItem[] = [];

  loading: boolean = false;
  pdfLoading: boolean = false;
  pdfError: boolean = false;
  pdfSource: SafeResourceUrl | null = null;
  selectedDoc: ImportedDocumentItem | null = null;
  downloadModalDocName: string = '';

  searchText: string = '';
  rangeDates: Date[] | null = null;
  maxDate: Date = new Date();
  orderTypeOptions = [
    { label: 'Contract Import', value: 'IMPORT_CONTRACT' },
  ];
  selectedOrderType: string = 'IMPORT_CONTRACT';

  pageNumber: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 25, 50];

  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();
  readonly DATE_FORMAT = 'YYYY-MM-DD';

  constructor(
    private readonly router: Router,
    private readonly orderService: OrderService,
    private readonly storageService: StorageService,
    private readonly genericHttpService: GenericHttpService,
    private readonly modalService: NgbModal,
    private readonly sanitizer: DomSanitizer,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly analyticsService: AnalyticsService
  ) {
    this.userRole = this.storageService.getRole() || null;
    this.accountId = this.storageService.getAccountId() || null;
    this.currentUserName = localStorage.getItem('username') || localStorage.getItem('name') || 'User';
  }

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe((val) => {
        this.searchText = val;
        this.pageNumber = 1;
        this.applyFiltersAndPaginate();
      });

    this.fetchDocuments();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.pdfSource) {
      this.pdfSource = null;
    }
  }

  fetchDocuments(): void {
    this.loading = true;

    const params: any = {
      action_type: 'individual',
      account_id: this.accountId || '',
      page_number: 1,
      page_size: 100, 
    };

    if (this.selectedOrderType && this.selectedOrderType !== 'ALL') {
      params['order_type'] = this.selectedOrderType;
    }

    this.orderService
      .getOrders(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading = false;
          let ordersList: any[] = [];

          if (resp && resp.status === 200 && Array.isArray(resp.content)) {
            ordersList = resp.content;
          } else if (resp && Array.isArray(resp.content)) {
            ordersList = resp.content;
          }

          if (this.selectedOrderType && this.selectedOrderType !== 'ALL') {
            ordersList = ordersList.filter((o: any) => {
              const ot = (o.order_type || o.order_type_display || '')
                .toString()
                .trim()
                .toUpperCase();
              return (
                ot === 'IMPORT_CONTRACT' ||
                ot === 'CONTRACT_IMPORT' ||
                ot === 'CONTRACT IMPORT' ||
                ot === 'CAT_IMPORT' ||
                ot === 'CAT-IMPORT'
              );
            });
          }

          this.documents = ordersList.map((order: any) =>
            this.mapOrderToDocument(order)
          );
          this.applyFiltersAndPaginate();
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.loading = false;
          console.error('Failed to fetch contract import orders:', err);
          this.documents = [];
          this.applyFiltersAndPaginate();
          this.cdr.detectChanges();
        },
      });
  }
   onColumnVisibilityChange(changes: ColumnVisibility[]): void {
    const tableConfiguration: { [key: string]: boolean } = {};

    changes.forEach((c) => {
      tableConfiguration[c.field] = c.visible;
    });

    this.orderService.saveUserSettings({
      table_configuration: tableConfiguration
    }).subscribe({
      next: (res: any) => {
        console.log('Settings Saved', res);
      },
      error: (err) => {
        console.error('Save Failed', err);
      },
    });
  }

  private mapOrderToDocument(order: any): ImportedDocumentItem {
    let docName =
      order.file_name ||
      order.document_name ||
      order.document_title ||
      order.name;

    if (!docName && order.referance_id) {
      docName = `${order.referance_id}.pdf`;
    }
    if (!docName) {
      docName = `Contract_Import_${order.id}.pdf`;
    }

    let importedBy =
      order.user_name ||
      order.user ||
      order.account?.account_name ||
      order.account_name ||
      this.currentUserName;

    if (typeof importedBy === 'object') {
      importedBy = importedBy.name || importedBy.username || this.currentUserName;
    }

    let status = order.status || order.status_display || 'Completed';
    if (status === 'WAITING_FOR_APPROVAL') {
      status = 'Approval Pending';
    }

    return {
      id: order.id,
      order_id: order.id,
      ref_id: order.referance_id || order.ref_id || '----',
      document_name: docName,
      uploaded_on: order.created || order.created_at || new Date(),
      imported_by: importedBy,
      status: status,
      order_type: order.order_type || 'IMPORT_CONTRACT',
      raw_order: order,
    };
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  onDateSelect(): void {
    if (this.rangeDates && this.rangeDates[0] && this.rangeDates[1]) {
      this.pageNumber = 1;
      this.applyFiltersAndPaginate();
    }
  }

  onDateRangeChange(evt: any): void {
    if (!evt || (Array.isArray(evt) && evt.length === 0)) {
      this.rangeDates = null;
      this.pageNumber = 1;
      this.applyFiltersAndPaginate();
    }
  }

  onOrderTypeChange(): void {
    this.pageNumber = 1;
    this.fetchDocuments();
  }

  applyFiltersAndPaginate(): void {
    let list = [...this.documents];

    if (this.searchText && this.searchText.trim()) {
      const q = this.searchText.trim().toLowerCase();
      list = list.filter((item) => {
        const nameMatch = (item.document_name || '').toLowerCase().includes(q);
        const orderIdMatch = (item.order_id || '').toString().toLowerCase().includes(q);
        const refIdMatch = (item.ref_id || '').toLowerCase().includes(q);
        const importedByMatch = (item.imported_by || '').toLowerCase().includes(q);
        return nameMatch || orderIdMatch || refIdMatch || importedByMatch;
      });
    }

    if (this.rangeDates && this.rangeDates[0]) {
      const start = moment(this.rangeDates[0]).startOf('day');
      const end = this.rangeDates[1]
        ? moment(this.rangeDates[1]).endOf('day')
        : moment(this.rangeDates[0]).endOf('day');

      list = list.filter((item) => {
        if (!item.uploaded_on) return false;
        const itemDate = moment(item.uploaded_on);
        return itemDate.isBetween(start, end, null, '[]');
      });
    }

    this.filteredDocuments = list;
    this.totalRecords = list.length;

    const startIdx = (this.pageNumber - 1) * this.pageSize;
    const endIdx = startIdx + this.pageSize;
    this.displayedDocuments = list.slice(startIdx, endIdx);
    this.cdr.detectChanges();
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.pageNumber = page;
    this.applyFiltersAndPaginate();
  }

  onPaginationChange(event: { page: number; rows: number }): void {
    this.pageNumber = event.page + 1;
    this.pageSize = event.rows;
    this.applyFiltersAndPaginate();
  }
  goBack() {
  this.router.navigate(['/doqfy/bulk-upload/form/cat-import']);
}
  
  onRowsPerPageChange(rows: number): void {
    this.pageSize = rows;
    this.pageNumber = 1;
    this.applyFiltersAndPaginate();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
  }

  get pageNumbersArray(): number[] {
    const pages: number[] = [];
    const total = this.totalPages;
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
    return pages;
  }

  get showingStart(): number {
    if (this.totalRecords === 0) return 0;
    return (this.pageNumber - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.pageNumber * this.pageSize, this.totalRecords);
  }

  viewDocument(doc: ImportedDocumentItem): void {

  this.selectedDoc = doc;
  this.pdfLoading = true;
  this.pdfError = false;
  this.pdfSource = null;

  this.analyticsService.trackEvent('click_view_contract_import_document', {
    order_id: doc.order_id,
    document_name: doc.document_name,
  });

  this.modalService.open(this.viewDocModal, {
    size: 'xl',
    centered: true,
    backdrop: 'static',
    keyboard: false,
  });
    

    this.orderService
      .getOrderDocument(Number(doc.order_id))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.pdfLoading = false;
          if (resp && resp.content && resp.content !== 'None') {
            const raw: string = resp.content;
            const cleaned =
              raw.startsWith("b'") && raw.endsWith("'") ? raw.slice(2, -1) : raw;
            try {
              const byteCharacters = atob(cleaned);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: 'application/pdf' });
              const url = URL.createObjectURL(blob);
              this.pdfSource = this.sanitizer.bypassSecurityTrustResourceUrl(url);
            } catch (e) {
              console.error('Error parsing PDF content:', e);
              this.pdfError = true;
            }
          } else {
            this.pdfError = true;
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.pdfLoading = false;
          this.pdfError = true;
          console.error('Error fetching order document:', err);
          this.cdr.detectChanges();
        },
      });
  }
  downloadDocument(doc: ImportedDocumentItem): void {
    this.selectedDoc = doc;
    this.downloadModalDocName = doc.document_name;

    this.analyticsService.trackEvent('click_download_contract_import_document', {
      order_id: doc.order_id,
      document_name: doc.document_name,
    });

    this.modalService.open(this.downloadSuccessModal, {
      centered: true,
      backdrop: 'static',
      keyboard: true,
    });

    this.orderService
      .getOrderDocument(Number(doc.order_id))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          if (resp && resp.content && resp.content !== 'None') {
            const raw: string = resp.content;
            const cleaned =
              raw.startsWith("b'") && raw.endsWith("'") ? raw.slice(2, -1) : raw;
            try {
              const byteCharacters = atob(cleaned);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: 'application/pdf' });
              saveAs(blob, doc.document_name || `Contract_${doc.order_id}.pdf`);
            } catch (e) {
              console.error('Error downloading document blob:', e);
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Document file not found for this order.',
            });
          }
        },
        error: (err: any) => {
          console.error('Download error:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Download Failed',
            detail: 'Failed to download document. Please try again.',
          });
        },
      });
  }

  closeModal(): void {
    this.modalService.dismissAll();
    this.pdfSource = null;
  }

  gotoContractImport(): void {
    this.router.navigate(['/doqfy/bulk-upload/form', 'cat-import']);
  }

  gotoDashboard(): void {
    if (this.userRole === 'CLIENT') {
      this.router.navigate(['/doqfy/dashboard']);
    } else {
      this.router.navigate(['/doqfy/admin-dashboard']);
    }
  }
  

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('complete') || s.includes('success')) {
      return 'completed';
    }
    if (s.includes('pending') || s.includes('wait') || s.includes('approval')) {
      return 'status-badge--pending';
    }
    if (s.includes('reject') || s.includes('fail') || s.includes('cancel')) {
      return 'status-badge--rejected';
    }
    return 'status-badge--inprogress';
  }
}
